import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'

type GhaAmenityType = 'parking' | 'wifi' | 'breakfast'

type BedbankAmenitiesPriorityVariant = 'default' | 'meta-search-v2'
type BedbankAmenity = App.OfferInclusion & {
  propertyIconName?: App.Property.IconName
}

const GHA_AMENITY_PRIORITY: Array<GhaAmenityType> = ['breakfast', 'parking', 'wifi']
const GHA_AMENITY_KEYWORDS: Record<GhaAmenityType, Array<string>> = {
  parking: ['parking', 'valet', 'self parking', 'car park', 'garage'],
  wifi: ['wifi', 'wi fi', 'internet', 'broadband', 'wireless', 'wlan'],
  breakfast: ['breakfast', 'half board', 'full board', 'all inclusive'],
}
const GHA_AMENITY_SYMBOLS: Record<GhaAmenityType, Array<string>> = {
  parking: ['parking', 'underground_parking', 'p_in_circle'],
  wifi: ['wifi', 'wifi_signal', 'internet_connection'],
  breakfast: ['breakfast', 'coffee_cup', 'cloche'],
}

/** Guest-experience amenities, highest first, for meta search mobile V2. */
const META_SEARCH_V2_PROMOTE_KEYWORDS: Array<Array<string>> = [
  ['gym', 'fitness'],
  ['pool', 'swimming'],
  ['spa', 'sauna', 'hammam', 'wellness', 'massage'],
  ['restaurant', 'dining'],
  ['bar', 'lounge', 'cocktail'],
  ['kitchen'],
]
/** Operational amenities that should sit below guest-experience ones on V2. */
const META_SEARCH_V2_DEMOTE_KEYWORDS = [
  'front desk',
  'reception',
  'housekeeping',
  'laundry',
  'concierge',
  '24 7',
]

function normalizeAmenityText(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ')
}

function matchesKeywords(description: string, keywords: Array<string>): boolean {
  const text = normalizeAmenityText(description)
  return keywords.some(keyword => text.includes(keyword))
}

function getGhaAmenityType(amenity: Pick<App.OfferInclusion, 'description' | 'symbol'>): GhaAmenityType | undefined {
  const text = normalizeAmenityText(amenity.description)
  const symbol = amenity.symbol?.toLowerCase()

  const hasMatch = (type: GhaAmenityType) => (
    GHA_AMENITY_KEYWORDS[type].some(keyword => text.includes(keyword)) ||
    (!!symbol && GHA_AMENITY_SYMBOLS[type].includes(symbol))
  )

  if (hasMatch('parking')) return 'parking'
  if (hasMatch('wifi')) return 'wifi'
  if (hasMatch('breakfast')) return 'breakfast'
}

function mapPopularFacilitiesToAmenities(popularFacilities: Array<string>): Array<BedbankAmenity> {
  return popularFacilities.map(facility => ({
    id: facility,
    description: facility,
    symbol: determineOfferFeatureSymbol(facility),
  }))
}

function sortRestAmenitiesForMetaSearchV2(
  restAmenities: Array<BedbankAmenity>,
): Array<BedbankAmenity> {
  const promoted: Array<BedbankAmenity | undefined> = Array.from({ length: META_SEARCH_V2_PROMOTE_KEYWORDS.length })
  const middle: Array<BedbankAmenity> = []
  const demoted: Array<BedbankAmenity> = []

  restAmenities.forEach((amenity) => {
    const promoteIndex = META_SEARCH_V2_PROMOTE_KEYWORDS.findIndex(keywords =>
      matchesKeywords(amenity.description, keywords),
    )
    if (promoteIndex >= 0 && !promoted[promoteIndex]) {
      promoted[promoteIndex] = amenity
      return
    }
    if (matchesKeywords(amenity.description, META_SEARCH_V2_DEMOTE_KEYWORDS)) {
      demoted.push(amenity)
      return
    }
    middle.push(amenity)
  })

  return [
    ...promoted.filter((amenity): amenity is BedbankAmenity => !!amenity),
    ...middle,
    ...demoted,
  ]
}

function prioritiseAmenityGroup(
  amenities: Array<BedbankAmenity>,
  seen: Set<string>,
  priorityVariant: BedbankAmenitiesPriorityVariant,
): Array<BedbankAmenity> {
  const prioritizedAmenities = new Map<GhaAmenityType, BedbankAmenity>()
  const restAmenities: Array<BedbankAmenity> = []

  amenities.forEach((amenity) => {
    const ghaAmenityType = getGhaAmenityType(amenity)
    const dedupeKey = ghaAmenityType
      ? `gha:${ghaAmenityType}`
      : `text:${normalizeAmenityText(amenity.description)}`

    if (seen.has(dedupeKey)) return
    seen.add(dedupeKey)

    if (ghaAmenityType) {
      prioritizedAmenities.set(ghaAmenityType, amenity)
      return
    }
    restAmenities.push(amenity)
  })

  const orderedRestAmenities = priorityVariant === 'meta-search-v2'
    ? sortRestAmenitiesForMetaSearchV2(restAmenities)
    : restAmenities

  return [
    ...GHA_AMENITY_PRIORITY.flatMap(type => prioritizedAmenities.get(type) ? [prioritizedAmenities.get(type)!] : []),
    ...orderedRestAmenities,
  ]
}

export default function getPrioritizedBedbankAmenities({
  shownRateInclusions = [],
  popularFacilities,
  facilityAmenities,
  backfillFacilityAmenities = false,
  priorityVariant = 'default',
}: {
  shownRateInclusions?: Array<App.OfferInclusion>
  popularFacilities: Array<string>
  facilityAmenities?: Array<BedbankAmenity>
  /** Only metasearch tiles use legacy facilities to fill gaps in property-content amenities. */
  backfillFacilityAmenities?: boolean
  priorityVariant?: BedbankAmenitiesPriorityVariant
}): Array<BedbankAmenity> {
  // Only let rate inclusions contribute to the GHA-typed slots
  // (parking / wifi / breakfast). The rate payload also carries
  // rate-metadata labels like "Room only" / "Non-refundable" that
  // would otherwise leak into the rest tail as fake amenities.
  const ghaShownRateInclusions = shownRateInclusions.filter(
    (inclusion) => getGhaAmenityType(inclusion) !== undefined,
  )

  const seen = new Set<string>()
  const legacyAmenities = mapPopularFacilitiesToAmenities(popularFacilities)
  const amenityGroups = facilityAmenities && backfillFacilityAmenities
    ? [facilityAmenities, ghaShownRateInclusions, legacyAmenities]
    : [[...ghaShownRateInclusions, ...(facilityAmenities ?? legacyAmenities)]]

  return amenityGroups.flatMap(amenities => prioritiseAmenityGroup(amenities, seen, priorityVariant))
}
